/**
 * Configurable tag printing utility for Zebra ZSB labels
 * Replaces hardcoded print logic in ProductForm and ProductsToUpdate
 */

// Owner decision 2026-07-13: the tag barcode encodes the SKU VERBATIM so a
// POS scan searches by SKU (Shopify POS matches SKU or barcode). CODE128
// accepts any length, so no checksum math and no fabricated numbers — the
// scanner always returns exactly what the label says. Products without a SKU
// fall back to the stored Shopify barcode so old labels stay printable.
function getBarcodeValue(product) {
  const sku = String(product.sku || '').trim().replace(/[^A-Za-z0-9._-]/g, '')
  if (sku) return sku

  const barcodeDigits = String(product.barcode || '').replace(/\D/g, '')
  if (barcodeDigits.length >= 4) return barcodeDigits
  return ''
}

// Back-compat alias (PostPublishTagPreviewModal imports this name).
const getEan8 = getBarcodeValue

function escapeHtml(str) {
  if (!str) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function normalizeNumberToken(value) {
  const text = String(value || '').trim().replace(',', '.')
  const n = Number.parseFloat(text)
  if (!Number.isFinite(n) || n <= 0) return ''
  return Number.isInteger(n) ? `${n}` : `${n.toFixed(2).replace(/\.?0+$/, '')}`
}

function normalizeColorToken(value) {
  const lower = String(value || '').toLowerCase()
  if (!/(gold|ouro|10k|14k|18k|22k|24k|white gold|yellow gold|rose gold)/i.test(lower)) return ''
  if (/white|branco/.test(lower)) return 'WG'
  if (/yellow|amarelo/.test(lower)) return 'YG'
  if (/rose|rosa|pink/.test(lower)) return 'RG'
  return ''
}

function buildTechnicalTagInfo(product) {
  const metafields = product?.metafields || {}
  const karatMatch = String(product?.karat || metafields?.karat || '').match(/(\d{1,2})/)
  const karatDigits = karatMatch ? karatMatch[1] : ''
  const materialText = [
    product?.title,
    metafields?.material,
    metafields?.color
  ].join(' ')
  const metalToken = /(silver|prata)/i.test(materialText)
    ? 'SIL'
    : /(platinum|platina)/i.test(materialText)
      ? 'PLAT'
      : karatDigits ? `AU${karatDigits}K` : 'AU'
  const colorToken = metalToken.startsWith('AU')
    ? normalizeColorToken([metafields?.material, metafields?.color].join(' '))
    : ''

  const typeRaw = String(product?.productType || '').trim()
  const typeToken = (typeRaw.split(/\s+/).find(Boolean) || 'JEWL')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6)

  const stoneCarat = normalizeNumberToken(metafields?.stone_carat)
  const stoneToken = stoneCarat ? `${stoneCarat}CT` : ''

  const weightValue = normalizeNumberToken(product?.weight || metafields?.weight || '')
  const weightToken = weightValue ? `${weightValue}G` : ''

  const base = [metalToken, colorToken, typeToken, stoneToken]
    .filter(Boolean)
    .join(' ')
  if (weightToken && `${base} ${weightToken}`.trim().length <= 24) {
    return `${base} ${weightToken}`.trim()
  }
  return base.slice(0, 24)
}

function getTagPrintInfo(product) {
  const raw = product?.tagPrintInfo || product?.metafields?.tag_print_info || ''
  const normalized = String(raw || '').trim().slice(0, 24)
  if (normalized) return normalized
  return buildTechnicalTagInfo(product)
}

/**
 * Generate HTML for printing tag labels
 * @param {Array} products - Array of product objects with { title, price, sku, barcode, weight, karat, productType }
 * @param {Object} tagConfig - Tag configuration from useTagConfig
 * @param {Object} options - Optional rendering options
 * @returns {string} Full HTML document string
 */
export function generateTagHtml(products, tagConfig, options = {}) {
  const cfg = tagConfig || {}
  const fields = cfg.fields || {}
  const fontSize = cfg.fontSize || {}
  const alignment = cfg.alignment || {}
  const barcodeCfg = cfg.barcode || {}
  const labelCfg = cfg.label || {}
  const previewMode = options.preview === true
  const previewScaleRaw = Number(options.previewScale)
  const previewScale = previewMode && Number.isFinite(previewScaleRaw)
    ? Math.min(3, Math.max(1, previewScaleRaw))
    : 1
  const previewGapPx = previewMode ? Math.max(8, Math.round(8 * previewScale)) : 10
  const previewExtraWidthPx = previewMode ? Math.max(0, Math.round((previewScale - 1) * 220)) : 0

  const labelWidth = labelCfg.width || '2.25in'
  const labelHeight = labelCfg.height || '0.5in'
  // CODE128 always: it encodes any SKU verbatim. (EAN8 would silently render
  // nothing for the shop's 4/5/8-digit SKUs that aren't checksum-valid EAN-8s.)
  const barcodeFormat = 'CODE128'
  const barcodeWidth = barcodeCfg.width || 0.7
  const barcodeHeight = barcodeCfg.height || 22
  const showBarcode = fields.barcode !== false && barcodeCfg.show !== false
  const showBarcodeNumber = fields.barcodeNumber !== false
  const leftAlign = alignment.left || 'left'
  const rightAlign = alignment.right || 'right'
  const leftAlignItems = leftAlign === 'center' ? 'center' : leftAlign === 'right' ? 'flex-end' : 'flex-start'
  const rightAlignItems = rightAlign === 'center' ? 'center' : rightAlign === 'right' ? 'flex-end' : 'flex-start'

  const labels = products.map((product, idx) => {
    const ean8 = getEan8(product)
    const price = product.price || '0'
    const tagPrintInfo = getTagPrintInfo(product)
    const title = tagPrintInfo || product.title || product.productType || 'Jewelry'
    const weightValue = product.weight || ''
    const karat = product.karat || product.metafields?.karat || ''
    const productType = product.productType || ''
    const sku = product.sku || ''

    const leftContent = []
    if (showBarcode && ean8) {
      leftContent.push(`<svg id="barcode-${idx}" class="barcode"></svg>`)
    }
    if (showBarcodeNumber && ean8) {
      leftContent.push(`<div class="sku" style="font-size:${fontSize.sku || 5.5}pt">#${escapeHtml(ean8)}</div>`)
    }
    if (fields.sku && sku) {
      leftContent.push(`<div class="sku" style="font-size:${fontSize.sku || 5.5}pt">${escapeHtml(sku)}</div>`)
    }
    if (fields.karat && karat) {
      leftContent.push(`<div class="meta" style="font-size:${fontSize.karat || 5}pt">${escapeHtml(karat)}K</div>`)
    }

    const rightContent = []
    if (fields.title !== false) {
      rightContent.push(`<div class="desc" style="font-size:${fontSize.title || 6}pt">${escapeHtml(title)}</div>`)
    }
    if (fields.productType && productType) {
      rightContent.push(`<div class="meta" style="font-size:${fontSize.productType || 5}pt">${escapeHtml(productType)}</div>`)
    }
    if (fields.weight !== false && weightValue) {
      rightContent.push(`<div class="weight" style="font-size:${fontSize.weight || 6}pt">${escapeHtml(String(weightValue))}g</div>`)
    }
    if (fields.price !== false) {
      rightContent.push(`<div class="price" style="font-size:${fontSize.price || 9}pt">$${parseFloat(price).toFixed(2)}</div>`)
    }

    return `
      <div class="label">
        <div class="left" style="text-align:${leftAlign};align-items:${leftAlignItems}">
          ${leftContent.join('\n')}
        </div>
        <div class="spacer"></div>
        <div class="right" style="text-align:${rightAlign};align-items:${rightAlignItems}">
          ${rightContent.join('\n')}
        </div>
      </div>
    `
  })

  const barcodeScripts = products.map((product, idx) => {
    const ean8 = getEan8(product)
    if (!showBarcode || !ean8) return ''
    return `try { if (window.JsBarcode) { JsBarcode("#barcode-${idx}", "${ean8}", { format: "${barcodeFormat}", width: ${barcodeWidth}, height: ${barcodeHeight}, displayValue: ${barcodeCfg.displayValue ? 'true' : 'false'}, margin: 0 }); } } catch(e) {}`
  }).filter(Boolean)

  return `<!DOCTYPE html>
<html>
<head>
  <title>Price Tags</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
  <style>
    @page { size: ${labelWidth} ${labelHeight}; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .label {
      width: ${labelWidth}; height: ${labelHeight};
      display: flex; justify-content: space-between; align-items: center;
      page-break-after: ${previewMode ? 'auto' : 'always'};
      border: ${previewMode ? '1px solid #d9dde4' : 'none'};
      border-bottom: ${previewMode ? '1px solid #d9dde4' : '1px dashed #ccc'};
      background: #fff;
    }
    .label:last-child {
      page-break-after: auto;
      border-bottom: ${previewMode ? '1px solid #d9dde4' : 'none'};
    }
    .left {
      width: 19mm; display: flex; flex-direction: column;
      justify-content: center;
      gap: 0.3mm; padding: 0.5mm 0.5mm;
    }
    .left > * { max-width: 100%; }
    .right > * { max-width: 100%; }
    .barcode { width: 18mm; height: 8mm; }
    .sku { color: #333; font-family: monospace; line-height: 1; }
    .meta { color: #444; line-height: 1; }
    .spacer { width: 19mm; height: ${labelHeight}; flex: 0 0 19mm; }
    .right {
      width: 19mm; display: flex; flex-direction: column;
      justify-content: center; gap: 0.3mm; padding: 0.5mm 0.5mm;
    }
    .price { font-weight: bold; line-height: 1; }
    .desc { font-weight: 500; line-height: 1.05; word-wrap: break-word; max-height: 6.5mm; overflow: hidden; }
    .weight { color: #444; line-height: 1; }
    @media print { .label { border: none; border-bottom: none; } }
    @media screen {
      body {
        background: ${previewMode ? 'transparent' : '#f0f0f0'};
        padding: ${previewMode ? '8px' : '20px'};
        display: ${previewMode ? 'flex' : 'block'};
        justify-content: ${previewMode ? 'flex-start' : 'initial'};
      }
      .label {
        margin-bottom: ${previewMode ? `${previewGapPx}px` : '10px'};
        box-shadow: ${previewMode ? 'none' : '0 1px 3px rgba(0,0,0,0.1)'};
        transform: ${previewMode ? `scale(${previewScale})` : 'none'};
        transform-origin: top left;
        margin-right: ${previewMode ? `${previewExtraWidthPx}px` : '0'};
      }
    }
  </style>
</head>
<body>
  ${labels.join('\n')}
  <script>
    window.addEventListener('load', () => {
      ${barcodeScripts.join('\n')}
    });
  <\/script>
</body>
</html>`
}

/**
 * Print tags by opening a new window with the generated HTML
 * @param {Array} products - Array of product objects
 * @param {Object} tagConfig - Tag configuration
 * @param {Object} options - Print window options { windowWidth, windowHeight }
 */
export function printTags(products, tagConfig, options = {}) {
  if (!products || products.length === 0) return

  const html = generateTagHtml(products, tagConfig)
  const requestedWidth = Number(options.windowWidth)
  const requestedHeight = Number(options.windowHeight)
  const windowWidth = Number.isFinite(requestedWidth) && requestedWidth > 0 ? Math.round(requestedWidth) : 400
  const windowHeight = Number.isFinite(requestedHeight) && requestedHeight > 0 ? Math.round(requestedHeight) : 600
  const printWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight}`)
  if (!printWindow) return

  printWindow.document.write(html)
  printWindow.document.close()

  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print()
    }, 500)
  }
}

export { getBarcodeValue, getEan8 }
